<template>
  <Grid
    class="rfr-visits-grid"
    :pageable="pageable"
    :data-items="visits"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="total"
    :columns="columns"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    :resizable="true"
    :sort="sort"
    :reorderable="true"
    selected-field="selected"
    ref="gridRef"
    @columnresize="onColumnResize"
    @columnreorder="onColumnReorder"
    @filterchange="filterChangeHandler"
    @pagechange="pageChangeHandler"
    @sortchange="sortChangeHandler"
    @selectionchange="changeSelection"
    @rowclick="changeSelection"
    @headerselectionchange="changeHeaderSelection"
    data-cy="rfr-visits-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e) => props.onSortchange(e)"
        @filterchange="(e) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:SelectCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <input
          v-if="selectionAvailable(dataItem.NonInformative == 1)"
          type="checkbox"
          v-model="dataItem[field]"
          @change="emit('selectionChanged', [dataItem])"
        />
      </td>
    </template>
    <template v-slot:VisitIdCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <div v-if="visitLinkEnabled(dataItem)">
          <a
            v-if="dataItem.RfrCreatedVisit && mode == EnumVisitMode.Inspection && !isRc"
            href="#"
            :class="{ saved: dataItem.HasSavedData }"
            class="visit-link"
            @click="redirectToRfrCreatedVisit(dataItem)"
            data-cy="visit-link"
          >
            Not Available
          </a>
          <a
            v-else
            :href="getVisitUrl(dataItem)"
            :class="{ saved: dataItem.HasSavedData }"
            class="visit-link"
            data-cy="visit-link"
          >
            {{ dataItem[field] }}
          </a>
        </div>
        <span v-else>{{ dataItem[field] }}</span>
      </td>
    </template>
    <template v-slot:TripIdCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <a :href="getTripUrl(dataItem)" class="trip-link" data-cy="trip-link">
          {{ dataItem[field] }}
        </a>
      </td>
    </template>
    <template v-slot:VisitTypeCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ enumHelper.splitByCapitalLetter(EnumVisitType[dataItem[field]]) }}
      </td>
    </template>
    <template v-slot:RfrNameCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <span
          :class="{
            red: dataItem.TeamLeaderName != dataItem.RfrName && props.isTeamLeader, // this is ONLY for team leader. RC should not see anything in red related to rfr teams
          }"
          >{{ dataItem[field] }}</span
        >
      </td>
    </template>
    <template v-slot:PlantNameCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem[field] }}
        <a
          :href="`/oudirect/PlantHistory/${dataItem.PlantId}`"
          target="_blank"
          class="plant-history-link"
          data-cy="plant-history-link"
        >
          <font-awesome-icon icon="history" aria-hidden="true" />
        </a>
      </td>
    </template>
    <template v-slot:StatusCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ !isRc && mode == EnumVisitMode.Inspection ? "Open" : dataItem[field] }}
      </td>
    </template>
    <template v-slot:CancelCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <Button type="button" theme-color="error" @click="cancelVisit(dataItem)" data-cy="cancel-button">
          Cancel
        </Button>
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnProps,
  type GridColumnReorderEvent,
  type GridColumnResizeEvent,
  type GridFilterChangeEvent,
  type GridHeaderSelectionChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
  type GridRowClickEvent,
  type GridSelectionChangeEvent,
} from "@progress/kendo-vue-grid";
import { computed, ref, watch, watchEffect } from "vue";
import type { CompositeFilterDescriptor, SortDescriptor } from "@progress/kendo-data-query";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { useMainStore } from "@/stores/useMainStore";
import { RfrGroup } from "@/shared/access/RoleGroups";
import { UserRoles } from "@/Domain/UserRoles";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import { Button } from "@progress/kendo-vue-buttons";
import moment from "moment";
import type { VisitDisplayExtended } from "@/api/rfrVisitsSearch";
import { EnumVisitMode } from "@/Domain/EnumVisitMode";
import enumHelper from "@/shared/helpers/enumHelper";
import {
  getFilterOperatorsForDate,
  getFilterOperatorsForEnum,
  getFilterOperatorsForNumber,
} from "@/shared/helpers/filtersHelper";
import { CacheService } from "@/shared/services/CacheService";
import GridController from "@/shared/services/GridController";
import { isMobile } from "@/shared/helpers/mobileHelper";

const main = useMainStore();

const props = defineProps<{
  visits: VisitDisplayExtended[];
  mode: EnumVisitMode;
  menuType: string;
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  isTeamLeader: boolean;
  getPropValues: (column: GridColumnPropsExtended) => Promise<VisitDisplayExtended[]>;
}>();

const emit = defineEmits<{
  (e: "visitsChanged", skip: number, take: number, filterEvent: GridFilterChangeEvent | null): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "selectionChanged", selectedItems: VisitDisplayExtended[]): void;
  (e: "cancelVisitClicked", visit: VisitDisplayExtended): void;
}>();

const gridName = "rfrVisitsGrid";

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const columns = ref<GridColumnPropsExtended[]>([]);

let pageSizes = [10, 20, 25, 50, 100, 200];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

const gridRef = ref<InstanceType<typeof Grid> | null>(null);

const isRc = computed(() => props.menuType == "VisitsAsRc");

const rfrNameColumnVisible = computed(() => {
  return (
    props.isTeamLeader ||
    (isRc.value && (main.isInRole(UserRoles.ROLE_RABBINIC_COORDINATOR) || main.isInRole(UserRoles.ROLE_GROUP_LEADER)))
  );
});

// RC needs to see the checkbox on EIR main or overdue bucket (either of first 2) if it's not informative.
// RC and RFR need to see checkbox on EFR
const selectionAvailable = function (nonInformative: boolean = true) {
  if (props.mode == EnumVisitMode.Financial) return true;

  return (
    (props.mode == EnumVisitMode.Inspection || props.mode == EnumVisitMode.Overdue) && isRc.value && nonInformative
  );
};

const getColumns = function () {
  const cols = [] as GridColumnPropsExtended[];
  const isRfr = main.isInAnyRole(RfrGroup.roles);

  if (selectionAvailable()) {
    cols.push({
      field: "selected",
      cell: "SelectCell",
      width: "50px",
      sortable: false,
      filterable: false,
      headerClassName: "hide-menu",
      hideInColumnMenu: true,
    });
  }

  cols.push({
    title: "Visit ID",
    cell: "VisitIdCell",
    field: "Id",
    columnMenu: "columnMenu",
    hideInColumnMenu: true,
    allowedFilterOperators: getFilterOperatorsForNumber(),
    minWidth: 70,
  });

  cols.push({
    title: "Plant Name",
    cell: "PlantNameCell",
    field: "PlantName",
    columnMenu: "columnMenu",
    minWidth: 100,
  });

  if (props.mode != EnumVisitMode.Payable) {
    cols.push({
      title: "From Date",
      field: "PeriodStartDate",
      columnMenu: "columnMenu",
      type: "date",
      format: "{0:MM/dd/yyyy}",
      allowedFilterOperators: getFilterOperatorsForDate(),
      minWidth: 90,
    });
    cols.push({
      title: "To Date",
      field: "PeriodEndDate",
      columnMenu: "columnMenu",
      type: "date",
      format: "{0:MM/dd/yyyy}",
      allowedFilterOperators: getFilterOperatorsForDate(),
      minWidth: 90,
    });
    cols.push({
      title: "City",
      field: "City",
      columnMenu: "columnMenu",
      minWidth: 100,
    });
    cols.push({
      title: "State",
      field: "State",
      columnMenu: "columnMenu",
      minWidth: 100,
    });
  } else {
    cols.push({
      title: "Trip ID",
      field: "TripId",
      cell: "TripIdCell",
      columnMenu: "columnMenu",
      allowedFilterOperators: getFilterOperatorsForNumber(),
      minWidth: 100,
    });
  }

  cols.push({
    title: "Visit Type",
    field: "Type",
    cell: "VisitTypeCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
    minWidth: 70,
  });

  if (isRfr || main.isInRole(UserRoles.ROLE_GROUP_LEADER)) {
    cols.push({
      title: "RC",
      field: "Rc",
      columnMenu: "columnMenu",
      minWidth: 70,
    });
  }

  if (rfrNameColumnVisible.value) {
    cols.push({
      title: "RFR",
      field: "RfrName",
      cell: "RfrNameCell",
      columnMenu: "columnMenu",
      minWidth: 70,
    });
  }

  if (props.mode != EnumVisitMode.Inspection) {
    cols.push({
      title: "Visit Date",
      field: "VisitDate",
      columnMenu: "columnMenu",
      type: "date",
      format: "{0:MM/dd/yyyy}",
      allowedFilterOperators: getFilterOperatorsForDate(),
      minWidth: 70,
    });
  }

  if (props.mode == EnumVisitMode.Payable) {
    cols.push({
      title: "Processed",
      field: "PaymentProcessed",
      columnMenu: "columnMenu",
      type: "date",
      format: "{0:MM/dd/yyyy h:mm a}",
      allowedFilterOperators: getFilterOperatorsForDate(),
      minWidth: 70,
    });
  }

  if (
    props.mode == EnumVisitMode.Historical ||
    (isRc.value && (props.mode == EnumVisitMode.Inspection || props.mode == EnumVisitMode.Overdue))
  ) {
    cols.push({
      title: "Status",
      field: "ReportStatus",
      cell: "StatusCell",
      columnMenu: "columnMenu",
      minWidth: 70,
    });
    cols.push({
      title: "Status Date",
      field: "StatusDate",
      columnMenu: "columnMenu",
      type: "date",
      format: "{0:MM/dd/yyyy h:mm a}",
      allowedFilterOperators: getFilterOperatorsForDate(),
      minWidth: 70,
    });
  }

  if (props.mode == EnumVisitMode.Historical) {
    cols.push({
      title: "Last Updated",
      field: "LastUpdateDate",
      columnMenu: "columnMenu",
      type: "date",
      format: "{0:MM/dd/yyyy h:mm a}",
      allowedFilterOperators: getFilterOperatorsForDate(),
      minWidth: 70,
    });
  }

  if (props.mode == EnumVisitMode.Inspection && props.menuType == "VisitsAsRfr") {
    cols.push({
      title: "Cancel",
      cell: "CancelCell",
      columnMenu: "columnMenu",
      minWidth: 80,
    });
  }

  cols.push({
    title: "Country",
    field: "Country",
    columnMenu: "columnMenu",
    minWidth: 70,
  });

  if (props.menuType == "VisitsAsRfr" && props.mode != EnumVisitMode.Financial && props.mode != EnumVisitMode.Payable) {
    cols.push({
      title: "Assigned Onsite/Virtual Ratio",
      field: "AssignedVisitRatio",
      columnMenu: "columnMenu",
      minWidth: 70,
    });
    cols.push({
      title: "YTD Visit History",
      field: "YearToDateVisitRatio",
      columnMenu: "columnMenu",
      minWidth: 70,
    });
  }

  if (props.mode == EnumVisitMode.Payable) {
    cols.push({
      title: "Rfr Expenses",
      field: "RfrExpenses",
      columnMenu: "columnMenu",
      format: "{0:c}",
      minWidth: 70,
    });
    cols.push({
      title: "Rfr Fees",
      field: "RfrFees",
      columnMenu: "columnMenu",
      format: "{0:c}",
      minWidth: 70,
    });
  }

  return cols;
};

columns.value = getColumns();
const controller = new GridController<VisitDisplayExtended>(gridName, columns.value);

controller.initOptions();

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("visitsChanged", 0, take.value, e);
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("visitsChanged", event.page.skip, event.page.take, null);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) {
    columns.value = columnsProps;
    GridOptionsService.saveVisibleColumns(gridName, columnsProps);
  }
  console.debug("RfrVisitsGrid.vue, onColumnSubmit", columnsProps);
};

const onColumnResize = function (e: GridColumnResizeEvent) {
  const col = GridOptionsService.getResizedColumn(e, columns.value);
  if (col.title) GridOptionsService.saveColumnWidth(gridName, col.title, e.newWidth);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  columns.value = e.columns;
  GridOptionsService.saveColumnsOrder(gridName, e.columns);
};

const cancelVisit = function (visit: VisitDisplayExtended) {
  emit("cancelVisitClicked", visit);
};

const getVisitUrl = function (visit: VisitDisplayExtended, hideVisitId = false) {
  let url = "";
  if (props.mode == EnumVisitMode.Financial) {
    url = `EFRTripExpenses?type=${EnumVisitMode[props.mode]}&mode=new`;

    if (visit.HasEfr) url += `&mode=view`;
  } else if (props.mode == EnumVisitMode.Payable) {
    url = `RfrTripLog?type=${EnumVisitMode[props.mode]}&mode=view`;
  }
  // inspection, historical, or overdue
  else {
    url = `RfrVisitInspectionForm?type=${EnumVisitMode[props.mode]}`;
    if (!visit.EnteredBy) url += `&mode=new`;
    else url += `&mode=view`;

    const visitType = visit.Type ?? EnumVisitType.Regular;
    let subType = "&subtype=short";
    if (
      visit.Type &&
      visit.RfrCreatedVisit != undefined &&
      visitType == EnumVisitType.Regular &&
      !visit.RfrCreatedVisit
    )
      subType = "&subtype=long";
    url += subType;
  }

  if (visit.Id) {
    return url + `&visitID=${hideVisitId ? "" : visit.Id}&menu=${props.menuType}`;
  }

  return "";
};

const redirectToRfrCreatedVisit = function (visit: VisitDisplayExtended) {
  const url = getVisitUrl(visit, true);
  CacheService.set("RfrVisitInspectionFormId", visit.Id);
  window.location.href = url;
};

const getTripUrl = function (visit: VisitDisplayExtended) {
  return `RfrTripLog?type=Payable&mode=view&visitID=${visit.Id}&RFRID=${main.userLogin}&menu=${props.menuType}&tripId=${visit.TripId}`;
};

const visitLinkEnabled = function (visit: VisitDisplayExtended) {
  //   link is enabled if:
  // '1) form was submitted electronically via OUDIRECT OR
  // '2) Form was never filled out and the current user is an RFR (filling out his for for the first time) and the form is still current (not 90 days old)
  // 'link is disabled if:
  // '3) Form was never filled out and the current user is an RFR Vaad/Team Leader and the visit belongs to another member of the team
  // This is not applicable for mode = financial (there are check boxes and we only show current data) OR
  // mode = payable (financila history - user cannot fill out form)
  // This is relevant for the History tab where we show visits up to two years old but do not want anyone to start submitting out old forms.
  // The History tab has visits within the last 2 years that are either still open or have been closed (for the RC) and for the RFR all submitted tickets and all non submitted tickets that are not current (current ones are in the EIR tab)

  const enableThisVisitforTeamLeader =
    props.isTeamLeader && (visit.RfrName == visit.TeamLeaderName || visit.EnableEirTeamVisitsForTeamLeader);
  if (
    !visit.Id ||
    props.mode == EnumVisitMode.Payable ||
    props.mode == EnumVisitMode.Financial ||
    visit.ReportStatus == "Canceled"
  )
    return false;

  // now we are at the default else case of makeVisitUrl which means the target is RfRVisitInspectionForm.
  // This page can only be invoked with a non-blank visitID
  // We never want to call PAGE_RFR_VISITS_FORM unless we have a valid visit ID

  // form was submitted electronically via OUDIRECT
  if (visit.EnteredBy?.toLowerCase() == "ou direct") return true;

  if (main.isInAnyRole(RfrGroup.roles) && (!props.isTeamLeader || enableThisVisitforTeamLeader))
    if (
      props.mode != EnumVisitMode.Historical ||
      new Date(visit.PeriodEndDate!) > moment().subtract(90, "days").toDate()
    )
      return true;

  return false;
};

const changeSelection = function (event: GridSelectionChangeEvent | GridRowClickEvent) {
  const visit = event.dataItem as VisitDisplayExtended;
  if (!visit || !selectionAvailable(visit.NonInformative == 1)) return;
  visit.selected = !visit.selected;
  emit("selectionChanged", [visit]);
};

const changeHeaderSelection = function (event: GridHeaderSelectionChangeEvent) {
  if (!selectionAvailable()) return;

  const checked = event.event.target.checked;
  props.visits
    .filter((v) => selectionAvailable(v.NonInformative == 1))
    .forEach((visit) => {
      visit.selected = checked;
    });
  emit("selectionChanged", props.visits);
};

defineExpose({
  getColumns,
});

watch(
  () => [rfrNameColumnVisible.value],
  () => {
    columns.value = getColumns();
    controller.columns.value = columns.value;
    controller.handleResize();
  }
);

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});

watchEffect(() => {
  if (isMobile() && gridRef.value && !controller.gridRef.value) {
    controller.setGridRef(gridRef);
  }
});
</script>

<style scoped lang="scss">
.k-grid {
  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }
}

.rfr-visits-grid {
  min-height: 400px;
  height: 800px;
  overflow-wrap: break-word;

  .visit-link,
  .trip-link {
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;

    &.saved {
      color: #28a745;
    }
  }

  .plant-history-link {
    color: #337ab7;
    cursor: pointer;
    padding: 4px;

    &:hover {
      color: #23527c;
    }
  }

  .red {
    color: red;
  }
}
</style>
